import { IsNotEmpty, IsOptional, IsString } from 'class-validator';

/**
 * Response DTO for RingCentral connection status
 */
export class RingCentralConnectionStatusDto {
	public connected!: boolean;
	public accountId?: string;
	public accountName?: string;
	public connectedDate?: string;
	public connectedByUserName?: string;
}

/**
 * Response DTO for RingCentral authorization URL
 */
export class RingCentralAuthUrlDto {
	public url!: string;
	public state!: string;
}

/**
 * Request DTO for RingCentral OAuth callback
 */
export class RingCentralCallbackDto {
	@IsString()
	@IsNotEmpty()
	public code!: string;

	@IsString()
	@IsNotEmpty()
	public state!: string;
}

/**
 * Response DTO for RingCentral access token
 */
export class RingCentralAccessTokenDto {
	public accessToken!: string;
	public endpointId!: string;
}

/**
 * Internal model for RingCentral authentication response
 */
export interface RingCentralAuthenticationResponseModel {
	token_type: 'Bearer';
	access_token?: string;
	expires_in?: number;
	refresh_token?: string;
	refresh_token_expires_in?: number;
	scope?: string;
	owner_id?: string;
	endpoint_id?: string;
	error?: string;
	error_description?: string;
}

/**
 * Response DTO for user dial status (click-to-dial capability)
 */
export class RingCentralUserDialStatusDto {
	public connected!: boolean;
	public canDial!: boolean;
	public needsUserAuth!: boolean;
	public userAuthenticated!: boolean;
	public phoneExtensionId!: string | null;
	public phoneExtensionNumber!: string | null;
	public callMode!: 'ringout' | 'callout';
	/**
	 * The user's own direct phone number from their Workd user record (E.164),
	 * used to prefill the RingOut "ring me first" number when nothing was
	 * remembered in the browser.
	 */
	public userPhoneNumber!: string | null;
}

/**
 * Response DTO for per-user RingCentral authentication status
 */
export class RingCentralUserAuthStatusDto {
	public authenticated!: boolean;
	public endpointId?: string;
}

/**
 * Request DTO for initiating a dial
 */
export class RingCentralDialRequestDto {
	@IsString()
	@IsNotEmpty()
	public phoneNumber!: string;

	@IsString()
	@IsOptional()
	public deviceId?: string;

	@IsString()
	@IsOptional()
	public forwardingNumberId?: string;

	@IsString()
	@IsOptional()
	public callMethod?: 'callout' | 'ringout';

	/** User's phone number for RingOut (the phone that will ring first) */
	@IsString()
	@IsOptional()
	public ringOutPhoneNumber?: string;
}

/**
 * Why a RingCentral request made on the user's behalf could not complete.
 *
 * - RECONNECT_REQUIRED: RingCentral rejected the user's sign-in (it expired or
 *   was revoked). The user has to connect RingCentral again.
 * - PERMISSION_DENIED: RingCentral answered 403 - the user's RingCentral role
 *   does not allow this. Only their RingCentral administrator can change it.
 * - UNAVAILABLE: RingCentral could not be reached or failed (network, 5xx,
 *   rate limiting). Trying again later may work; the sign-in is kept.
 *
 * These are deliberately carried in a 200 response body rather than as an
 * HTTP 401/403: the web client treats any 401 as an expired WORKD session and
 * logs the user out.
 */
export type RingCentralServiceErrorCode =
	| 'RECONNECT_REQUIRED'
	| 'PERMISSION_DENIED'
	| 'UNAVAILABLE';

export interface RingCentralServiceErrorModel {
	code: RingCentralServiceErrorCode;
	message: string;
}

/**
 * Dial failures the client reacts to, on top of the service errors above.
 *
 * - DEVICE_NOT_FOUND: RingCentral no longer knows the phone the call was
 *   placed from (a remembered phone was removed or replaced).
 * - DEVICE_REQUIRED: Call Out was requested without choosing a phone.
 * - NUMBER_REQUIRED: RingOut was requested without a number to ring first.
 * - INVALID_NUMBER: the RingOut "ring me first" number is not a full phone
 *   number (an extension number cannot be used).
 * - INVALID_REQUEST: RingCentral rejected the request (HTTP 400).
 */
export type RingCentralDialErrorCode =
	| RingCentralServiceErrorCode
	| 'DEVICE_NOT_FOUND'
	| 'DEVICE_REQUIRED'
	| 'NUMBER_REQUIRED'
	| 'INVALID_NUMBER'
	| 'INVALID_REQUEST';

/**
 * Response DTO for dial result
 */
export class RingCentralDialResponseDto {
	public success!: boolean;
	public sessionId?: string;
	public error?: string;
	/** Machine-readable reason when `success` is false */
	public errorCode?: RingCentralDialErrorCode;
	/** Whether the call was diverted to a different number */
	public diverted?: boolean;
	/** The phone number the call was diverted to (E.164 format) */
	public divertedTo?: string;
	/** The original phone number that was requested */
	public originalNumber?: string;
}

/**
 * Internal model for RingCentral RingOut response
 */
export interface RingCentralRingOutResponseModel {
	id?: string;
	uri?: string;
	status?: {
		callStatus?: string;
		callerStatus?: string;
		calleeStatus?: string;
	};
}

/**
 * Internal model for RingCentral CallOut response
 */
export interface RingCentralCallOutResponseModel {
	session?: {
		id?: string;
		origin?: {
			type?: string;
		};
		parties?: Array<{
			id?: string;
			status?: {
				code?: string;
			};
			direction?: string;
			to?: {
				phoneNumber?: string;
				name?: string;
			};
			from?: {
				phoneNumber?: string;
				name?: string;
			};
		}>;
	};
}

/**
 * Internal model for RingCentral account info response
 */
export interface RingCentralAccountInfoModel {
	id?: number;
	uri?: string;
	mainNumber?: string;
	operator?: {
		extensionNumber?: string;
		name?: string;
	};
	serviceInfo?: {
		uri?: string;
		brand?: {
			id?: string;
			name?: string;
		};
		servicePlan?: {
			id?: string;
			name?: string;
		};
	};
	setupWizardState?: string;
	status?: string;
}

/**
 * Internal model for RingCentral device from devices API
 */
export interface RingCentralDeviceModel {
	id: string;
	uri?: string;
	name?: string;
	type?: string;
	status?: string;
	serial?: string;
	computerName?: string;
	phoneLines?: Array<{
		id?: string;
		lineType?: string;
		phoneInfo?: {
			id?: number;
			phoneNumber?: string;
			paymentType?: string;
			type?: string;
			usageType?: string;
		};
	}>;
}

/**
 * Internal model for RingCentral devices API response
 */
export interface RingCentralDevicesResponseModel {
	records?: Array<RingCentralDeviceModel>;
}

/**
 * Internal model for RingCentral forwarding number from API
 */
export interface RingCentralForwardingNumberModel {
	id: string;
	uri?: string;
	phoneNumber?: string;
	label?: string;
	type?: string;
	features?: Array<string>;
	flipNumber?: string;
	device?: {
		id: string;
	};
}

/**
 * Internal model for RingCentral forwarding numbers API response
 */
export interface RingCentralForwardingNumbersResponseModel {
	records?: Array<RingCentralForwardingNumberModel>;
}

/**
 * Combined user device DTO for frontend (unified view)
 */
export class RingCentralUserDeviceDto {
	public deviceId!: string;
	public forwardingNumberId!: string | null;
	public name!: string;
	public type!: string;
	public phoneNumber!: string | null;
	public status!: string;
	public computerName!: string | null;
	/**
	 * The device is a kind Call Out can ring - a desk phone (HardPhone), an
	 * existing SIP phone (OtherPhone) or the legacy RingCentral Phone softphone
	 * (SoftPhone) - whether or not it is online right now. The RingCentral App
	 * cannot be dialed directly, so it never qualifies.
	 */
	public callOutCapable!: boolean;
	/** Call Out can ring this device right now (capable AND online) */
	public supportsCallOut!: boolean;
	public supportsRingOut!: boolean;
}

/**
 * Response DTO for the user's click-to-dial devices.
 *
 * Always returned with HTTP 200 - see RingCentralServiceErrorCode for why a
 * RingCentral failure travels in `error` rather than as an HTTP status.
 */
export class RingCentralUserDevicesResponseDto {
	public devices!: Array<RingCentralUserDeviceDto>;
	/**
	 * The user's only RingCentral "device" is the RingCentral App's WebRTC
	 * placeholder: they use the RingCentral App and have no desk phone or legacy
	 * RingCentral Phone app, so Call Out has nothing to ring and RingOut is the
	 * way to call.
	 */
	public appOnly!: boolean;
	public error?: RingCentralServiceErrorModel;
}

/**
 * Options for initiating a dial (used by client-side services)
 */
export interface RingCentralDialOptionsModel {
	callMethod?: 'callout' | 'ringout';
	deviceId?: string;
	forwardingNumberId?: string;
	/** User's phone number for RingOut (the phone that will ring first) */
	ringOutPhoneNumber?: string;
}

/**
 * Response DTO for RingCentral extensions list
 */
export class RingCentralExtensionDto {
	public extensionId!: string;
	public extensionNumber!: string;
	public name!: string;
	public type!: string; // 'User', 'Department', etc.
	public status!: string;
	public email?: string;
}

/**
 * Internal model for RingCentral extension from API
 */
export interface RingCentralExtensionModel {
	id?: number;
	uri?: string;
	extensionNumber?: string;
	name?: string;
	type?: string;
	status?: string;
	contact?: {
		firstName?: string;
		lastName?: string;
		email?: string;
	};
}

/**
 * Internal model for RingCentral extensions API response
 */
export interface RingCentralExtensionsResponseModel {
	records?: Array<RingCentralExtensionModel>;
	paging?: {
		page?: number;
		totalPages?: number;
		perPage?: number;
		totalElements?: number;
	};
}
